/* eslint-disable react/display-name */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { DateRangePicker, FocusedInputShape } from 'react-dates'
import 'react-dates/lib/css/_datepicker.css'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { PostChargeExceptions } from '../../../../api/models/ChargeException.type'
import {
  ChargeExceptionType,
  CustomExceptionType,
  Days,
} from '../../../../api/models/Common/JyrneyEnums'
import { Supplier } from '../../../../api/supplier/suppliers.types'
import { suppliersAPI } from '../../../../api/supplier/suppliersAPI'
import FormInput from '../../../FormFields/FormInput/FormInput'
import FormSelect from '../../../FormFields/Select/FormSelect'
import { Select } from '../../../FormFields/Select/Select'
import styles from './chargeOverrideStyle'
import { OverridesSchema } from './OverridesSchema'

interface FormData {
  name: string
  chargeExceptionTypeName: ChargeExceptionType
  customExceptionTypeName:
    | 'Always'
    | 'Date'
    | 'DateTime'
    | 'Day'
    | 'Time'
    | 'Weekday'
    | 'Weekend'
  appliesFromDate?: Date
  appliesToDate?: Date
  appliesFromTimeHour?: number
  appliesFromTimeMinute?: number
  appliesToTimeHour?: number
  appliesToTimeMinute?: number
  appliesOnDay?:
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday'
    | 'Sunday'
  isDefault: boolean
}

export interface CreateOverrideProps {
  openState: boolean
  setOpenState: React.Dispatch<React.SetStateAction<boolean>>
  onComplete: () => void
}
const CreateOverride: FC<CreateOverrideProps> = (props) => {
  const formMethods = useForm({
    resolver: yupResolver(OverridesSchema),
  })
  const { openState, setOpenState } = props

  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  const [type, setType] = useState<string>()
  const { mutateAsync: mutate } = useMutation({
    mutationFn: (payload: PostChargeExceptions) =>
      suppliersAPI.createChargeException(payload),
  })

  // Starting Dates
  const defaultFromDate = moment().startOf('day')
  const defaultToDate = moment().add(30, 'days').endOf('day')
  const [fromDate, setFromDate] = useState(defaultFromDate)
  const [toDate, setToDate] = useState(defaultToDate)
  const [allDay, setAllDay] = useState(false)
  //Date Input Config
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  )

  const resetTimeFields = () => {
    formMethods.unregister('appliesFromTimeHour')
    formMethods.unregister('appliesFromTimeMinute')
    formMethods.unregister('appliesToTimeHour')
    formMethods.unregister('appliesToTimeMinute')
  }
  const resetDateTimeFields = () => {
    formMethods.unregister('appliesFromDate')
    formMethods.unregister('appliesToDate')
    formMethods.unregister('appliesOnDay')
    resetTimeFields()
  }

  useEffect(() => {
    resetDateTimeFields()
    if (type === 'Date') {
      formMethods.setValue(
        'appliesFromDate',
        moment(fromDate).format('YYYY-MM-DD'),
      )
      formMethods.setValue('appliesToDate', moment(toDate).format('YYYY-MM-DD'))
    }
    if (type === 'Day') {
      formMethods.register('appliesOnDay')
    }
  }, [type])

  useEffect(() => {
    formMethods.setValue(
      'appliesFromDate',
      moment(fromDate).format('YYYY-MM-DD'),
    )
    formMethods.setValue('appliesToDate', moment(toDate).format('YYYY-MM-DD'))
  }, [fromDate, toDate])

  const handleDatesChange = (dates: any) => {
    dates.startDate && setFromDate(dates.startDate.startOf('day'))
    dates.endDate && setToDate(dates.endDate.endOf('day'))
  }

  const onSubmit = async (data: FormData, event: any) => {
    console.log(data)
    event.preventDefault()
    const payload = {
      chargeException: data,
      supplierId: supplier.id,
    }
    await mutate(payload)
    setOpenState(!openState)
    setType(undefined)
    setAllDay(false)
    formMethods.reset()
  }
  formMethods.register('appliesFromDate')
  formMethods.register('appliesToDate')
  formMethods.register('customExceptionTypeName')
  formMethods.register('appliesOnDay')
  return (
    <Drawer
      anchor={'right'}
      open={openState}
      onClose={() => {
        setOpenState(false)
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <section>
            <Grid container spacing={2} sx={styles.inputFormContainer}>
              <Grid item xs={12}>
                <Typography variant={'h6'} gutterBottom>
                  Create charge override
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <FormInput
                  name="name"
                  label="Name of Override"
                  defaultValue=""
                />
              </Grid>
              <Grid item xs={8}>
                <Select
                  label="Type"
                  options={CustomExceptionType}
                  defaultValue=""
                  onChange={(event) => {
                    formMethods.setValue(
                      'customExceptionTypeName',
                      event.target.value,
                    )
                    setType(event.target.value)
                  }}
                />
              </Grid>

              {(type === 'DateTime' || type === 'Date') && (
                <Grid item xs={10}>
                  <DateRangePicker
                    startDate={fromDate}
                    startDateId="tata-start-date"
                    endDate={toDate}
                    endDateId="tata-end-date"
                    onDatesChange={handleDatesChange}
                    focusedInput={focusedInput}
                    onFocusChange={(focusedInput) =>
                      setFocusedInput(focusedInput)
                    }
                    displayFormat="DD MMM YY"
                    hideKeyboardShortcutsPanel={true}
                    isOutsideRange={() => false}
                    showDefaultInputIcon
                    minimumNights={0}
                    showClearDates
                    readOnly
                    small
                    dayPickerNavigationInlineStyles={{ background: '#ffffff' }}
                  />
                </Grid>
              )}
              {type === 'Day' && (
                <Grid item xs={8}>
                  <FormSelect
                    name="appliesOnDay"
                    label="Day"
                    options={Days}
                    defaultValue=""
                    onChange={(event) =>
                      formMethods.setValue('appliesOnDay', event.target.value)
                    }
                  />
                </Grid>
              )}
              {(type === 'Day' || type === 'Weekday' || type === 'Weekend') && (
                <Grid item xs={12} sm={8}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => {
                          setAllDay(e.target.checked)
                          resetTimeFields()
                        }}
                      />
                    }
                    label="All day"
                    sx={styles.labelColour}
                  />
                </Grid>
              )}
              {!allDay &&
                (type === 'DateTime' ||
                  type === 'Time' ||
                  type === 'Day' ||
                  type === 'Weekday' ||
                  type === 'Weekend') && (
                  <>
                    <Grid
                      container
                      spacing={2}
                      direction="row"
                      alignItems="flex-start"
                      sx={styles.leftPadding}
                    >
                      <Grid item xs={1}>
                        <Box component={'h6'} sx={styles.fromToLabel}>
                          From:
                        </Box>
                      </Grid>
                      <Grid item>
                        <FormInput
                          name="appliesFromTimeHour"
                          label="HH"
                          inputProps={{
                            step: 1,
                            min: 0,
                            max: 23,
                            type: 'number',
                          }}
                          defaultValue={''}
                          disabled={allDay}
                        />
                      </Grid>
                      <Grid item>
                        <FormInput
                          name="appliesFromTimeMinute"
                          label="MM"
                          inputProps={{
                            step: 1,
                            min: 0,
                            max: 59,
                            maxLength: 2,
                            type: 'number',
                          }}
                          defaultValue={''}
                          disabled={allDay}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      direction="row"
                      alignItems="flex-start"
                      sx={styles.leftPadding}
                    >
                      <Grid item xs={1}>
                        <Box component={'h6'} sx={styles.fromToLabel}>
                          To:
                        </Box>
                      </Grid>
                      <Grid item>
                        <FormInput
                          name="appliesToTimeHour"
                          label="HH"
                          inputProps={{
                            step: 1,
                            min: 0,
                            max: 23,
                            maxLength: 2,
                            type: 'number',
                          }}
                          defaultValue={''}
                          disabled={allDay}
                        />
                      </Grid>
                      <Grid item>
                        <FormInput
                          name="appliesToTimeMinute"
                          label="MM"
                          inputProps={{
                            step: 1,
                            min: 0,
                            max: 59,
                            maxLength: 2,
                            type: 'number',
                          }}
                          defaultValue={''}
                          disabled={allDay}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
            </Grid>
          </section>
          <Grid
            container
            spacing={2}
            direction="row"
            sx={styles.buttonsContainer}
          >
            <Grid item style={{ paddingTop: '20px' }} xs={5}>
              <Button
                type="submit"
                variant="outlined"
                className={`${styles.round} ${styles['jyrneyYellow']}`}
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Drawer>
  )
}

export default CreateOverride
